
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Users, File, Trash2, Clock, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';

interface CollaborativeDocument {
  id: string;
  title: string;
  content: string;
  lastEdited: string;
  createdBy: string;
  collaborators: { id: string; name: string; email: string; role: 'editor' | 'viewer' | 'owner'; active: boolean }[];
  comments: { id: string; text: string; author: string; timestamp: string; resolved: boolean }[];
}

const CollaborativeDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<CollaborativeDocument[]>([]);
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  useEffect(() => {
    // Load saved collaborative documents from localStorage
    const loadDocuments = () => {
      try {
        const savedDocs = JSON.parse(localStorage.getItem('adaaltBotCollabDocs') || '[]');
        setDocuments(savedDocs);
      } catch (error) {
        console.error("Error loading collaborative documents:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Error loading documents"
        });
      }
    };

    loadDocuments();
    
    // Add event listener for storage changes (if user has multiple tabs open)
    window.addEventListener('storage', loadDocuments);
    
    // If no documents exist, create a sample document
    if (documents.length === 0) {
      const sampleDoc: CollaborativeDocument = {
        id: Date.now().toString(),
        title: 'Sample Collaborative Document',
        content: 'This is a collaborative document that multiple team members can edit together.',
        lastEdited: new Date().toISOString(),
        createdBy: 'Current User',
        collaborators: [
          { id: '1', name: 'Current User', email: 'user@example.com', role: 'owner', active: true }
        ],
        comments: []
      };
      
      setDocuments([sampleDoc]);
      localStorage.setItem('adaaltBotCollabDocs', JSON.stringify([sampleDoc]));
    }
    
    return () => {
      window.removeEventListener('storage', loadDocuments);
    };
  }, []);

  const createNewDocument = () => {
    const newDoc: CollaborativeDocument = {
      id: Date.now().toString(),
      title: 'New Collaborative Document',
      content: '',
      lastEdited: new Date().toISOString(),
      createdBy: 'Current User',
      collaborators: [
        { id: '1', name: 'Current User', email: 'user@example.com', role: 'owner', active: true }
      ],
      comments: []
    };
    
    const updatedDocs = [...documents, newDoc];
    setDocuments(updatedDocs);
    localStorage.setItem('adaaltBotCollabDocs', JSON.stringify(updatedDocs));
    
    // Navigate to new document
    navigate(`/collaborate/${newDoc.id}`);
    toast({
      title: "Success",
      description: "New collaborative document created"
    });
  };

  const deleteDocument = (id: string) => {
    const updatedDocs = documents.filter(doc => doc.id !== id);
    setDocuments(updatedDocs);
    localStorage.setItem('adaaltBotCollabDocs', JSON.stringify(updatedDocs));
    toast({
      title: "Success",
      description: "Document deleted"
    });
  };

  const openDocument = (id: string) => {
    navigate(`/collaborate/${id}`);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ur' ? 'ur-PK' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={`text-lg ${language === 'ur' ? "text-right" : "text-left"}`}>
          Collaborative Documents
        </CardTitle>
        <Button onClick={createNewDocument} className="flex items-center gap-1">
          <Plus size={16} />
          New Collaborative Document
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {documents.length === 0 ? (
          <div className="text-center py-8">
            <p className="mb-4">No collaborative documents yet</p>
            <Button onClick={createNewDocument} variant="outline">
              Create your first collaborative document
            </Button>
          </div>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.id}
              className="p-4 border rounded-md hover:border-primary transition-colors cursor-pointer"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1" onClick={() => openDocument(doc.id)}>
                  <h3 className="font-medium text-lg mb-1">{doc.title}</h3>
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <Clock size={14} className="mr-1" />
                    Last edited: {formatDate(doc.lastEdited)}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="text-xs">
                      <Users size={12} className="mr-1" />
                      {doc.collaborators.length} {doc.collaborators.length === 1 ? 'collaborator' : 'collaborators'}
                    </Badge>
                    {doc.comments.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {doc.comments.length} {doc.comments.length === 1 ? 'comment' : 'comments'}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDocument(doc.id)}
                    className="flex items-center gap-1"
                  >
                    <File size={16} />
                    Open
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600 flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteDocument(doc.id);
                    }}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default CollaborativeDocuments;
